import { FC, useEffect, useState } from 'react';
import { useContextSelector } from 'use-context-selector';

import { quickBuy } from '@/core/apis/order-manager-apis';
import { AppContext, setAppState } from '@/core/store/global/AppProvider';
import { MembershipProductSelector } from '@/core/store/membership-product/membership-product-states';
import { MembershipProductContext } from '@/core/store/membership-product/MembershipProductProvider';
import { setCartData } from '@/core/store/productContext/product-reducers';
import { productSelector } from '@/core/store/productContext/product-states';
import { Product, ProductVersion } from '@/core/types/product.types';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { addToCart, calculateTotal } from '@/ux-legacy/components/common/Cart/CartFunction';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';
type MembershipPurchaseProps = {
  membershipData: any;
  setShow: (show: boolean) => void;
};

const MembershipPurchase: FC<MembershipPurchaseProps> = ({ membershipData, setShow }) => {
  const [isPaymentLoading, setPaymentLoading] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  const [vendorConfig, setVendorConfig] = useState<any>();

  const dispatch = useContextSelector(AppContext, setAppState);

  const product: Product = useContextSelector(MembershipProductContext, MembershipProductSelector);

  const cartState = useContextSelector(AppContext, productSelector);

  const [selectedPlan, setSelectedPlan] = useState<ProductVersion | null>(product?.versions[0]);

  const [selectedOption, setSelectedOption] = useState(product?.versions[0]?.option1?.value);

  const onFormSubmit = async (values: any) => {
    if (product?.status == 2) {
      //inactive product
      setError('Product is Inactive');
      return;
    }

    if (!error && selectedPlan) {
      const qtyObj: any = {};

      setError(null);

      try {
        const lineItemPayload: any = {
          assetId: product?._id,
          versionId: selectedPlan?._id,
          quantity: 1,
          price: selectedPlan?.price?.fixedPrice,
        };

        const data: any = await addToCart(lineItemPayload, selectedPlan);

        if (!data) {
          setError(data?.error?.message);
          setTimeout(() => {
            setError(null);
          }, 3000);
        } else {
          /* eslint-disable-next-line no-unsafe-optional-chaining */
          const { total, discountAmount, subTotal, taxAmount } = calculateTotal([...cartState.dataObj, data]);
          dispatch(
            setCartData({
              /* eslint-disable-next-line no-unsafe-optional-chaining */
              dataObj: [...cartState?.dataObj, data],
              currencyCode: data?.currencyCode,
              amount: total, //data.totalAmount,
              subTotal,
              taxAmount,
              discountAmount: discountAmount,
            }),
          );
          setShow(false);
        }
      } catch (error: any) {
        setError(error?.message);
        setTimeout(() => {
          setError(null);
        }, 3000);
      }
    }
  };

  const quickBuyProduct = (version: ProductVersion) => {
    if (!version) return;
    const lineItemPayload: any = {
      assetId: product?._id,
      versionId: version?._id,
      quantity: 1,
      price: version?.price?.fixedPrice,
      paymentType: version?.price?.fixedPrice ? 'VIPPS' : 'FREE',
    };

    setPaymentLoading(true);
    quickBuy(lineItemPayload)
      .then((res: any) => {
        if (version?.price?.fixedPrice) {
          window.open(res?.paymentLink, '_self', 'noopener');
        } else {
          window.location.reload();
        }
      })
      .catch((error: any) => {
        setError(error?.response?.data?.message || error?.message);
        setTimeout(() => {
          setError(null);
        }, 3000);
      });
    // .finally(() => {
    setPaymentLoading(false);
    // });
  };

  const handleOptionChange = (event: any, version: ProductVersion) => {
    setSelectedOption(event.target.value);
    setSelectedPlan(version);
  };

  useEffect(() => {
    if (product?.vendor?.configuration) {
      setVendorConfig(product?.vendor?.configuration);
    }
  }, [product]);

  return (
    <div className="w-full h-full flex flex-col md:min-w-[500px]">
      <div className="w-full flex flex-col gap-2 py-2">
        <div className="w-full">
          <div className="h-[100px] relative">
            <img
              className="w-full h-full object-cover"
              src={
                membershipData?.bannerImage
                  ? appendImageRepoDomainIfNotExistInUrl(membershipData?.bannerImage)
                  : IMG_PLACEHOLDER
              }
              alt="vendor_banner"
            />
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 p-4">
          <div className="text-[26px] md:text-[32px] font-bold">{membershipData?.name}</div>
          <div className="text-base text-center">{membershipData?.longDescription}</div>
        </div>
        {product?.versions.map((version: any, index: number) => {
          return (
            <div className="flex flex-row gap-y-1 items-center" key={version._id}>
              <input
                id={`option-${index}`}
                type="radio"
                name="options"
                value={version?.option1?.value}
                checked={selectedOption === version?.option1?.value}
                onChange={(e) => handleOptionChange(e, version)}
                className="form-radio h-4 w-4 text-black mr-2 cursor-pointer"
              />
              <label
                htmlFor={`option-${index}`}
                className="flex justify-between w-full items-center space-x-3 cursor-pointer"
              >
                <span className="text-gray-700">{version.name}</span>
                <span className="text-[#323232] font-medium">
                  {version?.price?.fixedPrice && version?.price?.fixedPrice > 0
                    ? `${version?.price?.fixedPrice} ${product?.vendor?.currencyCode || ''}`
                    : 'Free'}
                </span>
              </label>
            </div>

            // <button
            //   key={version._id}
            //   className="w-full md:w-[200px] h-auto flex flex-col border border-gray-400 shadow-md rounded-md overflow-hidden justify-between"
            //   //   onClick={() => setSelectedPlan(version)}
            // >
            //   <div className="w-full h-auto flex flex-col p-4 text-left">
            //     <span className="text-2xl font-bold text-blue-500">{version.name}</span>
            //     {/* {version?.price?.fixedPrice <= 0 && (
            //       <div className="text-lg font-bold mt-2">
            //         Free
            //         <span className="text-base font-normal">
            //           {product?.validity !== 'LIFETIME'
            //             ? ` / ${product?.noOfMonths} ${product?.validity?.toUpperCase()}`
            //             : ` / ${product?.validity?.toUpperCase()}`}
            //         </span>
            //       </div>
            //     )}
            //     {version?.price?.fixedPrice > 0 && (
            //       <div className="text-lg font-bold mt-2">
            //         {product.vendor.currencyCode} {version?.price?.fixedPrice}
            //         <span className="text-base font-normal">
            //           {product?.validity !== 'LIFETIME'
            //             ? ` / ${product?.noOfMonths} ${product?.validity?.toUpperCase()}`
            //             : ` / ${product?.validity?.toUpperCase()}`}
            //         </span>
            //       </div>
            //     )} */}
            //     {/* <div className="text-sm text-gray-500">
            //       {product?.noOfMonths ? `${product?.noOfMonths} month(s)` : ''}
            //     </div> */}
            //     {/* <p className="py-2 text-sm text-left whitespace-pre-wrap">{version?.description}</p> */}
            //   </div>
            // </button>
          );
        })}
      </div>

      <button
        className={`w-full rounded-md text-white py-2 ${
          vendorConfig?.vipps || vendorConfig?.stripe
            ? 'bldr-cta-button hover:bg-slate-700'
            : 'bg-gray-400 cursor-not-allowed'
        }`}
        disabled={
          (vendorConfig?.vipps && !vendorConfig?.vipps) || (vendorConfig?.stripe && !vendorConfig?.stripe)
        }
        onClick={() => selectedPlan && onFormSubmit(selectedPlan)}
      >
        Add To Cart
      </button>
    </div>
  );
};

export default MembershipPurchase;
